import {
  CalendarDays,
  FileText,
  LogOut,
  Map,
  MessageSquare,
  Shield,
  Trophy,
  User,
  Users,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router";
import { toast } from "sonner";

import { ApiKeyDialog } from "@/components/api-key-dialog";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAdminKey } from "@/hooks/use-auth";
import { adminKeyStore } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { CourseScope } from "./course-scope";

/**
 * `scoped` marks the sections that act *within* one course, and so need the course
 * selector above them. Terms & courses manages the records that selector chooses
 * between, so it deliberately has no scope — showing a course picker there would
 * imply the page only concerned that course.
 */
const TABS = [
  { to: "/admin/challenges", label: "Challenges", icon: Trophy, scoped: true },
  { to: "/admin/students", label: "Students", icon: User, scoped: true },
  { to: "/admin/groups", label: "Teams", icon: Users, scoped: true },
  { to: "/admin/reports", label: "Reports", icon: FileText, scoped: true },
  { to: "/admin/slack", label: "Slack", icon: MessageSquare, scoped: true },
  // A maze belongs to nobody until a challenge points at it, so the library is not scoped.
  { to: "/admin/mazes", label: "Mazes", icon: Map, scoped: false },
  {
    to: "/admin/terms",
    label: "Terms & courses",
    icon: CalendarDays,
    scoped: false,
  },
];

/**
 * Underline tabs that scroll horizontally when they do not fit. On phones the
 * strip bleeds to the screen edge so a clipped label hints there is more, and
 * the active tab is scrolled into view when the section changes.
 */
function AdminTabs() {
  const { pathname } = useLocation();
  const activeRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    activeRef.current?.scrollIntoView({
      block: "nearest",
      inline: "center",
    });
  }, [pathname]);

  return (
    <nav
      className="-mx-4 flex gap-1 overflow-x-auto border-b px-4 [scrollbar-width:none] sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden"
      aria-label="Admin sections"
    >
      {TABS.map((tab) => {
        const active = pathname.startsWith(tab.to);
        const Icon = tab.icon;
        return (
          <NavLink
            key={tab.to}
            to={tab.to}
            ref={active ? activeRef : undefined}
            className={cn(
              "-mb-px flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium whitespace-nowrap transition-colors",
              active
                ? "border-primary text-foreground"
                : "text-muted-foreground hover:text-foreground border-transparent",
            )}
          >
            <Icon className="size-4 opacity-70" aria-hidden />
            {tab.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

function AdminGate() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mx-auto max-w-md py-12">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="size-4" aria-hidden />
            Admin access
          </CardTitle>
          <CardDescription>
            Required to manage challenges, keys, and teams.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button className="w-full" onClick={() => setOpen(true)}>
            Enter admin key
          </Button>
          <ApiKeyDialog mode="admin" open={open} onOpenChange={setOpen} />
        </CardContent>
      </Card>
    </div>
  );
}

export function AdminLayout() {
  const adminKey = useAdminKey();
  const { pathname } = useLocation();

  if (!adminKey) return <AdminGate />;

  const scoped =
    TABS.find((tab) => pathname.startsWith(tab.to))?.scoped ?? true;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Shield className="size-5" aria-hidden />
          Admin
        </h1>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            adminKeyStore.clear();
            toast.success("Admin key cleared");
          }}
        >
          <LogOut className="size-4" aria-hidden />
          Clear admin key
        </Button>
      </div>

      <AdminTabs />

      {/* Below the tabs, not above them: the selector belongs to the content it
          scopes, not to the console as a whole. */}
      {scoped ? (
        <div className="bg-muted/40 rounded-lg border px-3 py-2.5">
          <CourseScope />
        </div>
      ) : null}

      <Outlet />
    </div>
  );
}
